import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { ClipboardText, CheckCircle, X, Clock, Plus, ArrowsClockwise } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { fmtIstDate, fmtIstDateTime, todayIst } from '../lib/datetime';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const TYPE_LABEL = {
  late_arrival: 'Late Arrival',
  early_leave: 'Early Leave',
  custom_hours: 'Custom Hours',
  short_permission: 'Short Permission',
  wfh: 'Work From Home',
  other: 'Other',
};

const STATUS_META = {
  pending: { label: 'Pending', bg: '#fef3c7', fg: '#92400e', icon: Clock },
  approved: { label: 'Approved', bg: '#dcfce7', fg: '#15803d', icon: CheckCircle },
  rejected: { label: 'Rejected', bg: '#fee2e2', fg: '#b91c1c', icon: X },
};

export default function MyPermissionsPage() {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all'); // all / pending / approved / rejected
  const [open, setOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    request_type: 'late_arrival',
    date: todayIst(),
    requested_start: '',
    requested_end: '',
    duration_minutes: 0,
    reason: '',
  });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API_URL}/api/my/permission-requests`, { withCredentials: true });
      setList(r.data || []);
    } catch (e) {
      toast.error('Failed to load: ' + (e.response?.data?.detail || e.message));
    }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const filtered = list.filter((p) => filter === 'all' || p.status === filter);
  const counts = {
    pending: list.filter((p) => p.status === 'pending').length,
    approved: list.filter((p) => p.status === 'approved').length,
    rejected: list.filter((p) => p.status === 'rejected').length,
  };

  const submit = async () => {
    if (!form.reason.trim()) return toast.error('Please add a reason');
    setSubmitting(true);
    try {
      await axios.post(`${API_URL}/api/my/permission-requests`, form, { withCredentials: true });
      toast.success('Request submitted — admin will review shortly');
      setOpen(false);
      setForm({
        request_type: 'late_arrival', date: todayIst(),
        requested_start: '', requested_end: '', duration_minutes: 0, reason: '',
      });
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message);
    }
    setSubmitting(false);
  };

  return (
    <div className="space-y-4" data-testid="my-permissions-page">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h2 className="text-2xl font-bold flex items-center gap-2" style={{ color: '#1a2e4a' }}>
            <ClipboardText size={24} weight="duotone" /> My Permission Requests
          </h2>
          <p className="text-xs text-gray-500 mt-0.5">
            Track every permission you've raised and whether admin approved or rejected it.
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={load} data-testid="my-perm-refresh">
            <ArrowsClockwise size={14} className="mr-1" /> Refresh
          </Button>
          <Button onClick={() => setOpen(true)} data-testid="my-perm-new">
            <Plus size={14} className="mr-1" /> New Request
          </Button>
        </div>
      </div>

      {/* Summary tiles */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {[
          ['all', 'TOTAL', list.length, '#1a2e4a', '#f1f5f9'],
          ['pending', 'PENDING', counts.pending, '#92400e', '#fef3c7'],
          ['approved', 'APPROVED', counts.approved, '#15803d', '#dcfce7'],
          ['rejected', 'REJECTED', counts.rejected, '#b91c1c', '#fee2e2'],
        ].map(([k, label, n, fg, bg]) => (
          <button
            key={k}
            onClick={() => setFilter(k)}
            className={`text-left rounded-lg p-3 transition-all ${filter === k ? 'ring-2 ring-emerald-500' : ''}`}
            style={{ background: bg }}
            data-testid={`my-perm-tile-${k}`}
          >
            <div className="text-[10px] font-bold uppercase tracking-wider" style={{ color: fg }}>{label}</div>
            <div className="text-2xl font-bold" style={{ color: fg }}>{n}</div>
          </button>
        ))}
      </div>

      {/* List */}
      {loading ? (
        <Card className="p-6 text-center text-sm text-gray-500">Loading…</Card>
      ) : filtered.length === 0 ? (
        <Card className="p-8 text-center text-sm text-gray-500" data-testid="my-perm-empty">
          {filter === 'all'
            ? "You haven't submitted any permission requests yet."
            : `No ${filter} requests.`}
        </Card>
      ) : (
        <div className="space-y-2">
          {filtered.map((p) => {
            const sm = STATUS_META[p.status] || STATUS_META.pending;
            const SIcon = sm.icon;
            return (
              <Card key={p.id} className="p-4 border-l-4" style={{ borderLeftColor: sm.fg }} data-testid={`my-perm-${p.id}`}>
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div className="flex-1 min-w-[200px]">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-sm font-bold" style={{ color: '#1a2e4a' }}>
                        {TYPE_LABEL[p.request_type] || p.request_type}
                      </span>
                      <span className="text-xs text-gray-500">·</span>
                      <span className="text-xs text-gray-700">{fmtIstDate(p.date)}</span>
                      {(p.requested_start || p.requested_end) && (
                        <span className="text-[11px] font-mono px-1.5 py-0.5 rounded bg-slate-100 text-slate-700">
                          {p.requested_start || '—'} → {p.requested_end || '—'}
                        </span>
                      )}
                      {p.duration_minutes > 0 && (
                        <span className="text-[11px] font-mono px-1.5 py-0.5 rounded bg-slate-100 text-slate-700">
                          {p.duration_minutes} min
                        </span>
                      )}
                    </div>
                    <div className="text-[13px] text-gray-700 mt-1">{p.reason}</div>
                    <div className="text-[10px] text-gray-400 mt-1">
                      Submitted {fmtIstDateTime(p.requested_at || p.created_at || '')}
                    </div>
                  </div>
                  <div className="flex flex-col items-end gap-1">
                    <span
                      className="inline-flex items-center gap-1 text-[10px] font-bold uppercase px-2.5 py-1 rounded-full"
                      style={{ background: sm.bg, color: sm.fg }}
                      data-testid={`my-perm-status-${p.id}`}
                    >
                      <SIcon size={12} weight="fill" /> {sm.label}
                    </span>
                    {p.status !== 'pending' && p.decided_at && (
                      <div className="text-[10px] text-gray-400 text-right">
                        {p.status === 'approved' ? 'Approved' : 'Rejected'} {fmtIstDateTime(p.decided_at)}
                      </div>
                    )}
                    {p.status === 'rejected' && p.decision_notes && (
                      <div className="text-[11px] text-rose-700 bg-rose-50 px-2 py-1 rounded max-w-xs">
                        <strong>Reason:</strong> {p.decision_notes}
                      </div>
                    )}
                  </div>
                </div>
              </Card>
            );
          })}
        </div>
      )}

      {/* New request modal */}
      {open && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setOpen(false)}>
          <Card className="w-full max-w-md bg-white p-5" onClick={(e) => e.stopPropagation()} data-testid="my-perm-dialog">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-lg font-bold" style={{ color: '#1a2e4a' }}>New Permission Request</h3>
              <button onClick={() => setOpen(false)} className="text-gray-400"><X size={18} /></button>
            </div>
            <div className="space-y-3">
              <div>
                <Label className="text-xs">Type</Label>
                <select
                  className="w-full border rounded px-2 py-2 text-sm mt-1"
                  value={form.request_type}
                  onChange={(e) => setForm({ ...form, request_type: e.target.value })}
                  data-testid="my-perm-type"
                >
                  {Object.entries(TYPE_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                </select>
              </div>
              <div>
                <Label className="text-xs">Date</Label>
                <Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} data-testid="my-perm-date" />
              </div>
              {form.request_type === 'short_permission' ? (
                <div>
                  <Label className="text-xs">Duration (minutes)</Label>
                  <Input
                    type="number"
                    min="15"
                    step="15"
                    value={form.duration_minutes}
                    onChange={(e) => setForm({ ...form, duration_minutes: Number(e.target.value) })}
                    data-testid="my-perm-duration"
                  />
                </div>
              ) : (form.request_type === 'late_arrival' || form.request_type === 'early_leave' || form.request_type === 'custom_hours') && (
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <Label className="text-xs">Start time</Label>
                    <Input type="time" value={form.requested_start} onChange={(e) => setForm({ ...form, requested_start: e.target.value })} data-testid="my-perm-start" />
                  </div>
                  <div>
                    <Label className="text-xs">End time</Label>
                    <Input type="time" value={form.requested_end} onChange={(e) => setForm({ ...form, requested_end: e.target.value })} data-testid="my-perm-end" />
                  </div>
                </div>
              )}
              <div>
                <Label className="text-xs">Reason</Label>
                <textarea
                  className="w-full border rounded px-2 py-2 text-sm mt-1 min-h-[80px]"
                  value={form.reason}
                  onChange={(e) => setForm({ ...form, reason: e.target.value })}
                  placeholder="Why do you need this permission?"
                  data-testid="my-perm-reason"
                />
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-4">
              <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
              <Button onClick={submit} disabled={submitting} data-testid="my-perm-submit">
                {submitting ? 'Submitting…' : 'Submit Request'}
              </Button>
            </div>
          </Card>
        </div>
      )}
    </div>
  );
}
